import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { ShoppingBag, PoundSterling, X, Briefcase, Search, Loader2, MapPin, Star, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../components/Button';
import Card from '../components/Card';
import UserName from '../components/UserName';
import supabase from '../lib/supabase';
import MarketplaceContact from '../components/messaging/MarketplaceContact';

interface PriceData {
  selected: boolean;
  price?: number;
  priceType?: 'hourly' | 'fixed' | null;
}

function OpenToWorkSection() {
  const { user } = useAuthStore();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [userListings, setUserListings] = useState<any[]>([]);
  const [selectedProfessions, setSelectedProfessions] = useState<Record<string, PriceData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const userProfessions = user?.professions || [];

  useEffect(() => {
    if (!isCollapsed) {
      fetchUserListings();
    }
  }, [isCollapsed]);

  const fetchUserListings = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const { data, error: fetchError } = await supabase
        .from('service_listings')
        .select(`
          *,
          provider:provider_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url,
            bio,
            experience_level,
            goals,
            location,
            other_profession
          )
        `)
        .eq('provider_id', user.id);

      if (fetchError) throw fetchError;
      
      const listings = data || [];
      setUserListings(listings);
      
      // Initialize state from user's professions and existing listings
      const initialState: Record<string, PriceData> = {};
      userProfessions.forEach(profession => {
        // Find existing listing for this profession
        const existingListing = listings.find(l => l.category === profession);
        if (existingListing) {
          initialState[profession] = {
            selected: true,
            price: existingListing.price,
            priceType: existingListing.price_type
          };
        } else {
          initialState[profession] = { selected: false, priceType: null };
        }
      });
      setSelectedProfessions(initialState);
    } catch (err: any) {
      console.error('Error fetching user listings:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // Get existing listing IDs
      const existingIds = userListings.map(listing => listing.id);

      // First, update messages to remove references to listings that will be deleted
      if (existingIds.length > 0) {
        const { error: updateError } = await supabase
          .from('messages')
          .update({ marketplace_listing_id: null })
          .in('marketplace_listing_id', existingIds);

        if (updateError) throw updateError;
      }

      // Then delete existing listings
      if (existingIds.length > 0) {
        const { error: deleteError } = await supabase
          .from('service_listings')
          .delete()
          .in('id', existingIds);

        if (deleteError) throw deleteError;
      }

      // Create new listings for each selected profession
      const selectedJobs = Object.entries(selectedProfessions)
        .filter(([_, value]) => value.selected)
        .map(([profession, value]) => {
          // Get the display name for the profession
          const displayName = profession === 'Other' && user.other_profession 
            ? user.other_profession 
            : profession;

          return {
            provider_id: user.id,
            title: `Available for ${displayName} work`,
            description: `I am available for work as a ${displayName}.`,
            category: profession,
            price: value.priceType ? value.price : null,
            price_type: value.priceType,
            availability: true
          };
        });


      const { error: insertError } = await supabase
        .from('service_listings')
        .insert(selectedJobs);

      if (insertError) throw insertError;

      // Refresh listings
      fetchUserListings();
      setIsCollapsed(true);
    } catch (error: any) {
      console.error('Error updating listings:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getExperienceBadgeColor = (level?: string) => {
    switch (level?.toLowerCase()) {
      case 'beginner':
        return 'bg-neutral-light text-neutral';
      case 'intermediate':
        return 'bg-accent-light text-accent';
      case 'professional':
        return 'bg-accent bg-opacity-20 text-accent';
      case 'veteran':
        return 'bg-secondary bg-opacity-20 text-secondary';
      default:
        return 'bg-neutral-light text-neutral';
    }
  };

  if (!user || userProfessions.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <div 
        className="flex items-center justify-between p-4 bg-neutral rounded-full cursor-pointer"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center">
          <Briefcase className="w-5 h-5 mr-2 text-surface-light" />
          <h2 className="text-lg font-semibold font-dillan text-surface-light">Open to Work</h2>
        </div>
        {isCollapsed ? (
          <ChevronDown className="w-5 h-5 text-surface-light" />
        ) : (
          <ChevronUp className="w-5 h-5 text-surface-light" />
        )}
      </div>

      {!isCollapsed && (
        <div className="mt-2 p-4 bg-primary-light rounded-xl border border-primary-light">
          {isLoading ? (
            <div className="flex justify-center items-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-secondary-dark bg-opacity-10 text-secondary-dark p-4 rounded-xl">
                  {error}
                </div>
              )}

              <p className="text-neutral pl-2">Select the professions you're available for and optionally set your price:</p>

              <div className="space-y-4">
                {userProfessions.map((profession) => {
                  const displayName = profession === 'Other' && user?.other_profession 
                    ? user.other_profession 
                    : profession;

                  return (
                    <div key={profession} className="p-4 rounded-full border border-surface-light bg-surface-light">
                      <div className="flex items-center pl-4">
                        <input
                          type="checkbox"
                          id={profession}
                          checked={selectedProfessions[profession]?.selected || false}
                          onChange={(e) => setSelectedProfessions(prev => ({
                            ...prev,
                            [profession]: { 
                              ...prev[profession],
                              selected: e.target.checked 
                            }
                          }))}
                          className="w-4 h-4 pl-2 border-2 border-primary rounded-full 
      appearance-none transition-all 
      checked:bg-primary checked:border-primary
      "
                        />
                        <label htmlFor={profession} className="ml-3 text-primary font-medium">
                          {displayName}
                        </label>
                      </div>

                      {selectedProfessions[profession]?.selected && (
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center space-x-3">
                            <label className="flex items-center space-x-1">
                              <input
                                type="checkbox"
                                checked={selectedProfessions[profession]?.priceType === 'hourly'}
                                onChange={() => setSelectedProfessions(prev => ({
                                  ...prev,
                                  [profession]: {
                                    ...prev[profession],
                                    priceType: 'hourly'
                                  }
                                }))}
                                className="w-3 h-3 border-2 border-neutral rounded-full 
      appearance-none transition-all 
      checked:bg-neutral checked:border-neutral
      "
                              />
                              <span className="text-sm text-neutral">Hourly Rate</span>
                            </label>
                            <label className="flex items-center space-x-1">
                              <input
                                type="checkbox"
                                checked={selectedProfessions[profession]?.priceType === 'fixed'}
                                onChange={() => setSelectedProfessions(prev => ({
                                  ...prev,
                                  [profession]: {
                                    ...prev[profession],
                                    priceType: 'fixed'
                                  }
                                }))}
                                className="w-3 h-3 border-2 border-neutral rounded-full 
      appearance-none transition-all 
      checked:bg-neutral checked:border-neutral"
                              />
                              <span className="text-sm text-neutral">Fixed Price</span>
                            </label>
                            <label className="flex items-center space-x-1">
                              <input
                                type="checkbox"
                                checked={!selectedProfessions[profession]?.priceType}
                                onChange={() => setSelectedProfessions(prev => ({
                                  ...prev,
                                  [profession]: {
                                    ...prev[profession],
                                    priceType: null,
                                    price: undefined
                                  }
                                }))}
                                className="w-3 h-3 border-2 border-neutral rounded-full 
      appearance-none transition-all 
      checked:bg-neutral checked:border-neutral"
                              />
                              <span className="text-sm text-neutral">No Price</span>
                            </label>
                          </div>
                          {selectedProfessions[profession]?.priceType && (
                            <div className="flex items-center justify-center space-x-2">
                              <div className="relative">
                                <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
                                <input
                                  type="number"
                                  value={selectedProfessions[profession]?.price || ''}
                                  onChange={(e) => setSelectedProfessions(prev => ({
                                    ...prev,
                                    [profession]: {
                                      ...prev[profession],
                                      price: parseFloat(e.target.value)
                                    }
                                  }))}
                                  className="w-36 h-10 pl-8 pr-2 rounded-full border border-secondary bg-secondary text-primary placeholder:text-primary focus:border-secondary-dark focus:ring-0 focus:outline-none"
                                  placeholder={`${selectedProfessions[profession]?.priceType === 'hourly' ? 'Hourly rate' : 'Fixed price'}`}
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 bg-neutral text-secondary hover:bg-primary-dark hover:text-primary border border-neutral hover:border-primary-dark"
                  onClick={() => setIsCollapsed(true)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1 bg-neutral text-secondary hover:bg-primary-dark hover:text-primary border border-neutral hover:border-primary-dark"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

export default function Marketplace() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [listings, setListings] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(() => {
    return sessionStorage.getItem('marketplaceSearchQuery') || '';
  });
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[] | null>(() => {
    const savedResults = sessionStorage.getItem('marketplaceSearchResults');
    return savedResults ? JSON.parse(savedResults) : null;
  });
  const [showContactModal, setShowContactModal] = useState<{
    providerId: string;
    listingId: string;
    message: string;
  } | null>(null);

  const normalizeKeys = (match: any) => {
    return {
      listing_id: match["Listing ID"] || match["listing_id"],
      provider_id: match["Provider ID"] || match["provider_id"],
      category: match["Category"] || match["category"],
      location: match["Location"] || match["location"],
      price: match["Price"] || match["price"],
      price_type: match["Price Type"] || match["price_type"],
      experience_level: match["Experience Level"] || match["experience_level"],
      goals: match["Goals"] || match["goals"],
      community_names: match["Community Names"] || match["community_names"],
      bio: match["Bio"] || match["bio"],
      title: match["Title"] || match["title"],
      description: match["Description"] || match["description"],
    };
  };

  const fetchListings = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('service_listings')
        .select(`
          *,
          provider:provider_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url,
            bio,
            experience_level,
            goals,
            location,
            other_profession
          )
        `)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setListings(data || []);
    } catch (err: any) {
      console.error('Error fetching listings:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      sessionStorage.setItem('marketplaceSearchQuery', searchQuery);
    } else {
      sessionStorage.removeItem('marketplaceSearchQuery');
    }
  }, [searchQuery]);

  useEffect(() => {
    if (searchResults) {
      sessionStorage.setItem('marketplaceSearchResults', JSON.stringify(searchResults));
    } else {
      sessionStorage.removeItem('marketplaceSearchResults');
    }
  }, [searchResults]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError("Please enter a description of what you're looking for");
      return;
    }

    try {
      setIsSearching(true);
      setError(null);
      setSearchResults(null);

      const response = await fetch("https://ai-matchmaking-43rd6vitg-camilles-projects-7de9d588.vercel.app/match", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description: searchQuery }),
      });

      if (!response.ok) {
        throw new Error("Failed to get recommendations");
      }

      const data = await response.json();
      if (data.recommendations?.matches && Array.isArray(data.recommendations.matches)) {
        const transformedResults = await Promise.all(
          data.recommendations.matches
            .map(async (match: any) => {
              const normalizedMatch = normalizeKeys(match);
              
              // Skip if this is the current user's listing
              if (normalizedMatch.provider_id === user?.id) {
                return null;
              }

              const { data: providerData } = await supabase
                .from("user_profiles")
                .select("*")
                .eq("id", normalizedMatch.provider_id)
                .single();

              return {
                ...normalizedMatch,
                provider: providerData || null,
              };
            })
        );

        // Filter out null results (current user's listings) and empty values
        const filteredResults = transformedResults.filter(result => result !== null);

        // Record the search and its results
        if (user) {
          const listingIds = filteredResults.map(result => result.listing_id).filter(Boolean);
          await supabase.rpc('record_service_listing_search', {
            search_text: searchQuery,
            listing_ids: listingIds
          });
        }

        setSearchResults(filteredResults);
        sessionStorage.setItem("marketplaceSearchResults", JSON.stringify(filteredResults));

        if (filteredResults.length === 0) {
          setError("No matching professionals found. Please try adjusting your search criteria.");
        }
      } else {
        setError("No matching professionals found. Please try adjusting your search criteria.");
        setSearchResults([]);
        
        // Record unsuccessful search
        if (user) {
          await supabase.rpc('record_service_listing_search', {
            search_text: searchQuery,
            listing_ids: []
          });
        }
      }
    } catch (err: any) {
      console.error("Search error:", err);
      setError("Failed to process your request. Please try again later.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleContact = (providerId: string, listingId: string) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    // Get the listing ID from either search results or regular listings
    const listing = searchResults ? 
      searchResults.find(result => result.listing_id === listingId || result.id === listingId) :
      listings.find(listing => listing.id === listingId);

    if (!listing) {
      console.error('Listing not found:', listingId);
      return;
    }

    // Show contact modal with pre-filled message
    setShowContactModal({
      providerId,
      listingId: listing.listing_id || listing.id, // Handle both search results and regular listings
      message: searchQuery || ''
    });
  };

  const userListings = listings.filter(listing => listing.provider_id === user?.id);
  const displayListings = searchResults || listings.filter(listing => listing.provider_id !== user?.id); // Filter out current user's listings from regular view

  const getExperienceBadgeColor = (level?: string) => {
    switch (level?.toLowerCase()) {
      case 'beginner':
        return 'bg-neutral-light text-neutral';
      case 'intermediate':
        return 'bg-accent-light text-accent';
      case 'professional':
        return 'bg-accent bg-opacity-20 text-accent';
      case 'veteran':
        return 'bg-secondary bg-opacity-20 text-secondary';
      default:
        return 'bg-neutral-light text-neutral';
    }
  };

  return (
    <div className="min-h-screen p-4 bg-secondary-dark">
      <div className="flex justify-center items-center space-y-4">
        <h1 className="text-3xl font-bold font-dillan text-primary mb-6 ">Marketplace</h1>
      </div>
      <div className="space-y-4">
        <OpenToWorkSection />
  
        <Card className="bg-neutral">
          <div className="space-y-4">
            <div className="flex items-center text-surface-light">
              <Search className="w-5 h-5 mr-2" />
              <h2 className="text-lg font-semibold font-dillan text-surface-light">Looking to Hire?</h2>
            </div>
            <ul className="list-disc list-inside text-surface-light text-m pl-2 space-y-1">
              <li>What kind of professional are you seeking?</li>
              <li>Where do you need this service?</li>
              <li>What's your budget range?</li>
              <li>What skills and experience are required?</li>
              <li>What kind of person would be a good fit?</li>
            </ul>
            <div className="flex justify-left px-0">
              <textarea
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-24 py-3 px-2 pl-4 rounded-full border border-primary-light bg-primary-light text-primary placeholder:text-primary focus:border-primary-light focus:ring-0 focus:outline-none resize-none"
                placeholder="Please answer the above questions to help us find the best creative match for your needs."
              />
            </div>
            
            <div className="flex items-center justify-end px-0">
              <Button
                variant="primary"
                onClick={handleSearch}
                disabled={isSearching || !searchQuery.trim()}
                className="w-42 text-sm text-surface-light mt-4"
              >
                {isSearching ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-0 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-3 h-3 mr-0" />
                    Find Matches
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
  
        {error && (
          <div className="bg-secondary-dark bg-opacity-10 text-secondary-dark p-4 rounded-xl">
            {error}
          </div>
        )}
  
        {searchResults && (
          <div className="flex items-center justify-end">
            <h2 className="text-lg font-semibold font-dillan text-primary">
              ({searchResults.length}) {searchResults.length === 1 ? 'match' : 'matches'}
            </h2>
          </div>
        )}
  
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
          </div>
        ) : searchResults === null ? (
          <div className="text-center py-2">
          </div>
        ) : displayListings.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="w-16 h-16 text-neutral mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium text-primary mb-2">
              No matching professionals found
            </h3>
            <p className="text-neutral">
              Try adjusting your search criteria or check back later.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayListings.map((listing) => {
              // Ensure we have the correct listing ID regardless of data source
              const listingId = listing.listing_id || listing.id;
              
              // Get the display name for the profession
              const displayName = listing.category === 'Other' && listing.provider.other_profession
                ? listing.provider.other_profession
                : listing.category;
              
              return (
                <Card key={listingId} className="bg-primary-light">
                  <div className="p-0 flex flex-col h-full">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <img
                            src={listing.provider.avatar_url || `https://ui-avatars.com/api/?name=${listing.provider.display_name || listing.provider.full_name}`}
                            alt={listing.provider.display_name || listing.provider.full_name}
                            className="w-10 h-10 rounded-full mr-3"
                          />
                          <div>
                          <UserName
                            name={listing.provider.display_name || listing.provider.full_name}
                            verified={listing.provider.verified}
                            size="md"
                            userId={listing.provider.id}
                            className="text-neutral"
                          />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-start items-center text-primary pl-14 font-bold">
                              {displayName}
                    </div>
                    <div className="text-sm text-primary mb-3 pl-14">
                      {listing.provider.experience_level && (
                        <>
                          {listing.provider.experience_level.charAt(0).toUpperCase() +
                            listing.provider.experience_level.slice(1)}
                        </>
                      )}
                      {listing.price && listing.price_type && (
                        <>
                          {listing.provider.experience_level && ' - '}
                          £{listing.price}
                          {listing.price_type === 'hourly' ? '/hr' : ' fixed'}
                        </>
                      )}
                    </div>
                    {listing.provider.location && (
                      <div className="flex items-center text-sm text-primary mb-2">
                        <MapPin className="w-4 h-4 mr-1.5" />
                        {listing.provider.location}
                      </div>
                    )}
                    <div className="flex justify-end mt-auto">
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-42 bg-neutral text-secondary hover:bg-primary-dark hover:text-primary border border-neutral hover:border-primary-dark"
                        onClick={() => handleContact(listing.provider_id, listingId)}
                      >
                        Contact
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-10">
          <div className="bg-neutral rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="bg-secondary-dark p-4 border border-secondary-dark flex items-center justify-between">
              <h2 className="text-xl font-bold font-dillan text-primary">Contact Provider</h2>
              <button
                onClick={() => setShowContactModal(null)}
                className="p-2 "
              >
                <X className="w-5 h-5 text-primary" />
              </button>
            </div>
            
            <div className="p-4">
              <MarketplaceContact
                recipientId={showContactModal.providerId}
                listingId={showContactModal.listingId}
                jobDescription={showContactModal.message}
                onClose={() => setShowContactModal(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}